/**
 * Benchmark Family Engine — Market Benchmark Certification (Sections 6 & 9).
 *
 * Storage is not the only charge that must be compared against the *right*
 * market. A rotator must be compared against the heavy rotator market, a
 * recovery van against the heavy recovery support-vehicle market, a rigger
 * against heavy recovery labor — not against a generic towing average.
 *
 * This module resolves the benchmark family for any invoice charge from the
 * charge description, the persisted service type, and the classified vehicle,
 * and validates that the resolved family is compatible with that vehicle class
 * (a passenger-scoped family may never be used on a heavy-duty commercial
 * claim, and vice versa).
 *
 * Deterministic and pure — no network, no AI. It never produces, alters, or
 * implies a market value; it only names the market a value belongs to.
 */

import type { VehicleClass } from "./vehicle-classification";
import { VEHICLE_CLASS_LABELS } from "./vehicle-classification";

export type BenchmarkFamilyKey =
  | "heavy_rotator"
  | "heavy_recovery_equipment"
  | "recovery_support_vehicle"
  | "commercial_tractor"
  | "rollback_flatbed"
  | "recovery_labor"
  | "recovery_cleanup"
  | "recovery_supplies"
  | "recovery_mileage"
  | "fuel_surcharge"
  | "administrative_fee"
  | "storage"
  | "light_duty_towing"
  | "general_service";

/** Which market a family belongs to. Used for class-compatibility checks. */
export type BenchmarkMarketScope = "commercial_heavy" | "commercial" | "passenger" | "class_agnostic";

export interface BenchmarkFamily {
  key: BenchmarkFamilyKey;
  /** Customer-facing market name. Contains no dataset or engine identifiers. */
  label: string;
  scope: BenchmarkMarketScope;
}

const FAMILY_BASE: Record<BenchmarkFamilyKey, { label: string; scope: BenchmarkMarketScope }> = {
  heavy_rotator: { label: "Rotator Recovery", scope: "commercial_heavy" },
  heavy_recovery_equipment: { label: "Recovery Equipment", scope: "commercial_heavy" },
  recovery_support_vehicle: { label: "Recovery Support Vehicle", scope: "commercial" },
  commercial_tractor: { label: "Commercial Tractor / Towing Unit", scope: "commercial" },
  rollback_flatbed: { label: "Rollback / Flatbed Transport", scope: "commercial" },
  recovery_labor: { label: "Recovery Labor & Personnel", scope: "class_agnostic" },
  recovery_cleanup: { label: "Site Cleanup & Equipment Cleaning", scope: "class_agnostic" },
  recovery_supplies: { label: "Rigging & Recovery Supplies", scope: "class_agnostic" },
  recovery_mileage: { label: "Recovery Mileage", scope: "class_agnostic" },
  fuel_surcharge: { label: "Recovery Fuel Surcharge", scope: "class_agnostic" },
  administrative_fee: { label: "Administrative & Release Fees", scope: "class_agnostic" },
  storage: { label: "Vehicle Storage", scope: "class_agnostic" },
  light_duty_towing: { label: "Light-Duty Towing", scope: "passenger" },
  general_service: { label: "General Service", scope: "class_agnostic" },
};

/** Vehicle classes whose charges belong to the heavy commercial markets. */
const HEAVY_CLASSES: ReadonlySet<VehicleClass> = new Set<VehicleClass>([
  "heavy_duty_commercial",
  "semi_tractor",
  "bus",
  "construction_equipment",
  "agricultural_equipment",
]);

const COMMERCIAL_CLASSES: ReadonlySet<VehicleClass> = new Set<VehicleClass>([
  "commercial",
  "straight_truck",
  "trailer",
  "rv",
  "specialty",
]);

export function isHeavyClass(vehicleClass: VehicleClass): boolean {
  return HEAVY_CLASSES.has(vehicleClass);
}
export function isCommercialClass(vehicleClass: VehicleClass): boolean {
  return HEAVY_CLASSES.has(vehicleClass) || COMMERCIAL_CLASSES.has(vehicleClass);
}

/**
 * Prefix a family label with the market tier implied by the vehicle class, so
 * the customer sees the actual market used ("Commercial Heavy-Duty Rotator
 * Recovery") rather than a generic family name.
 */
function tierPrefix(vehicleClass: VehicleClass, scope: BenchmarkMarketScope): string {
  if (scope === "passenger") return "Private Passenger";
  if (isHeavyClass(vehicleClass)) return "Commercial Heavy-Duty";
  if (COMMERCIAL_CLASSES.has(vehicleClass)) return "Commercial";
  if (vehicleClass === "private_passenger" || vehicleClass === "motorcycle") return "Private Passenger";
  return "";
}

const RULES: Array<{ key: BenchmarkFamilyKey; re: RegExp }> = [
  { key: "storage", re: /\bstorage\b/i },
  {
    key: "recovery_labor",
    re: /\brigger\b|\boperator\b|\bhelper\b|\bextra\s*man\b|\blabor\b|\btechnician\b|\bcrew\b|\bpersonnel\b|\bman\s*hour\b/i,
  },
  { key: "heavy_rotator", re: /\brotator\b|\b\d{2,3}\s*ton\b/i },
  {
    key: "heavy_recovery_equipment",
    re: /\bheavy\s*(duty)?\s*(wrecker|recovery|tow)|\bwinch(ing)?\b|\blandoll\b|\blowboy\b|\bair\s*cushion\b|\bunderlift\b|\bheavy\s*unit\b/i,
  },
  {
    key: "recovery_support_vehicle",
    re: /\brecovery\s*van\b|\bsupport\s*(unit|truck|vehicle|van)\b|\bservice\s*(truck|van)\b|\bsupervisor\s*(truck|unit)\b/i,
  },
  { key: "commercial_tractor", re: /\broad\s*tractor\b|\btractor\s*(unit)?\b|\bsemi\s*(tractor|unit)\b/i },
  { key: "rollback_flatbed", re: /\brollback\b|\bflat\s*bed\b|\bflatbed\b|\bcarrier\s*unit\b/i },
  {
    key: "recovery_cleanup",
    re: /\bclean(ing|up)?\b|\bdecon(tamination)?\b|\bsweep\b|\babsorb(ent|ant)\b|\bdebris\b|\bspill\b|\bhazmat\b/i,
  },
  { key: "recovery_supplies", re: /\btarp\b|\bstrap\b|\bchain\b|\bsuppl(y|ies)\b|\bdolly\b|\bair\s*bag\b|\bmaterials?\b/i },
  { key: "recovery_mileage", re: /\bmileage\b|\bper\s*mile\b|\bloaded\s*mile\b|\bdeadhead\b/i },
  { key: "fuel_surcharge", re: /\bfuel\b|\bsurcharge\b/i },
  {
    key: "administrative_fee",
    re: /\badmin(istrative)?\b|\bgate\s*fee\b|\brelease\s*fee\b|\bpaperwork\b|\bprocessing\b|\bnotification\b|\bnotice\b|\bfiling\b/i,
  },
  { key: "light_duty_towing", re: /\blight\s*duty\b|\bbasic\s*tow\b/i },
];

const SERVICE_TYPE_HINTS: Array<{ key: BenchmarkFamilyKey; re: RegExp }> = [
  { key: "storage", re: /storage/i },
  { key: "heavy_recovery_equipment", re: /heavy/i },
  { key: "recovery_labor", re: /labor/i },
  { key: "light_duty_towing", re: /light/i },
  { key: "administrative_fee", re: /^fee$/i },
];

export interface BenchmarkFamilyInput {
  description?: string | null;
  serviceType?: string | null;
  vehicleClass: VehicleClass;
  /** Storage market label resolved by the storage benchmark engine, if any. */
  storageMarketLabel?: string | null;
}

export interface ResolvedBenchmarkFamily extends BenchmarkFamily {
  /** Label including the market tier, e.g. "Commercial Heavy-Duty Rotator Recovery". */
  marketLabel: string;
  vehicleClass: VehicleClass;
  vehicleClassLabel: string;
}

/** Resolve the benchmark market family for a single invoice charge. */
export function classifyBenchmarkFamily(input: BenchmarkFamilyInput): ResolvedBenchmarkFamily {
  const text = `${input.description ?? ""}`.trim();
  let key: BenchmarkFamilyKey | null = null;

  for (const rule of RULES) {
    if (rule.re.test(text)) {
      key = rule.key;
      break;
    }
  }
  if (!key) {
    const st = `${input.serviceType ?? ""}`;
    key = SERVICE_TYPE_HINTS.find((h) => h.re.test(st))?.key ?? null;
  }
  if (!key) key = /\btow(ing)?\b|\brecovery\b/i.test(text) ? "heavy_recovery_equipment" : "general_service";

  // A light-duty family can never describe a heavy-duty commercial recovery;
  // the same unit on a heavy claim is a commercial rollback/transport charge.
  if (key === "light_duty_towing" && isCommercialClass(input.vehicleClass)) key = "rollback_flatbed";
  // Conversely, heavy families are meaningless on a passenger claim.
  if (
    (key === "heavy_rotator" || key === "heavy_recovery_equipment") &&
    (input.vehicleClass === "private_passenger" || input.vehicleClass === "motorcycle")
  ) {
    key = "light_duty_towing";
  }

  const base = FAMILY_BASE[key];
  const prefix = tierPrefix(input.vehicleClass, base.scope);
  const label =
    key === "storage" && input.storageMarketLabel ? input.storageMarketLabel : base.label;
  // Avoid a duplicated tier word ("Commercial Heavy-Duty Commercial Tractor").
  const baseLabel = prefix.startsWith("Commercial")
    ? base.label.replace(/^Commercial\s+/i, "")
    : base.label;
  const marketLabel =
    key === "storage" && input.storageMarketLabel
      ? input.storageMarketLabel
      : prefix
        ? `${prefix} ${baseLabel}`
        : base.label;

  return {
    key,
    label,
    scope: base.scope,
    marketLabel,
    vehicleClass: input.vehicleClass,
    vehicleClassLabel: VEHICLE_CLASS_LABELS[input.vehicleClass],
  };
}

/* ─────────────────── internal validation rules (Section 9) ─────────────────── */

export type BenchmarkFamilyIssueCode =
  | "passenger_family_on_commercial_vehicle"
  | "heavy_family_on_passenger_vehicle"
  | "unknown_vehicle_class"
  | "missing_family";

export interface BenchmarkFamilyIssue {
  code: BenchmarkFamilyIssueCode;
  severity: "fail" | "warn";
  charge: string;
  message: string;
}

/**
 * Internal certification rule: the benchmark family used for a charge must be
 * compatible with the classified vehicle. Reported internally only — it never
 * changes a market value.
 */
export function validateBenchmarkFamily(args: {
  charge: string;
  family: ResolvedBenchmarkFamily;
  vehicleClass: VehicleClass;
}): BenchmarkFamilyIssue[] {
  const { charge, family, vehicleClass } = args;
  const issues: BenchmarkFamilyIssue[] = [];

  if (vehicleClass === "unknown") {
    issues.push({
      code: "unknown_vehicle_class",
      severity: "warn",
      charge,
      message: "Vehicle class is unresolved, so the benchmark family cannot be certified against it.",
    });
    return issues;
  }

  if (family.scope === "passenger" && isCommercialClass(vehicleClass)) {
    issues.push({
      code: "passenger_family_on_commercial_vehicle",
      severity: "fail",
      charge,
      message: `${family.label} is a passenger market and cannot be applied to a ${VEHICLE_CLASS_LABELS[vehicleClass]}.`,
    });
  }

  if (
    family.scope === "commercial_heavy" &&
    (vehicleClass === "private_passenger" || vehicleClass === "motorcycle")
  ) {
    issues.push({
      code: "heavy_family_on_passenger_vehicle",
      severity: "fail",
      charge,
      message: `${family.label} is a heavy commercial market and cannot be applied to a ${VEHICLE_CLASS_LABELS[vehicleClass]}.`,
    });
  }

  return issues;
}

export interface BenchmarkFamilyAuditRow {
  charge: string;
  family: ResolvedBenchmarkFamily;
  issues: BenchmarkFamilyIssue[];
}

export interface BenchmarkFamilyAudit {
  pass: boolean;
  vehicleClass: VehicleClass;
  vehicleClassLabel: string;
  rows: BenchmarkFamilyAuditRow[];
  issues: BenchmarkFamilyIssue[];
}

/** Audit every charge on a claim against its benchmark family. Internal use. */
export function auditBenchmarkFamilies(args: {
  vehicleClass: VehicleClass;
  charges: Array<{ description?: string | null; serviceType?: string | null }>;
  storageMarketLabel?: string | null;
}): BenchmarkFamilyAudit {
  const rows = args.charges.map((c) => {
    const charge = (c.description ?? "").trim() || "Unlabeled charge";
    const family = classifyBenchmarkFamily({
      description: c.description,
      serviceType: c.serviceType,
      vehicleClass: args.vehicleClass,
      storageMarketLabel: args.storageMarketLabel ?? null,
    });
    return { charge, family, issues: validateBenchmarkFamily({ charge, family, vehicleClass: args.vehicleClass }) };
  });
  const issues = rows.flatMap((r) => r.issues);
  return {
    pass: !issues.some((i) => i.severity === "fail"),
    vehicleClass: args.vehicleClass,
    vehicleClassLabel: VEHICLE_CLASS_LABELS[args.vehicleClass],
    rows,
    issues,
  };
}
